import React, { useState, useCallback } from 'react';
import { Loader2 } from 'lucide-react';
import DictationTextField from './DictationTextField';
import { reflectionsApi } from '../api/services';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { cn } from '@/lib/utils';

interface GuidedReflectionDialogProps {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  date?: string;
  trigger?: string;
}

const REFLECTION_PROMPTS = [
  { section: 'Wins', prompt: 'What went well today? What are you proud of?', icon: '🏆' },
  { section: 'Challenges', prompt: 'What was difficult? What didn\'t go as planned?', icon: '⚡' },
  { section: 'Learnings', prompt: 'What did you learn? What would you do differently?', icon: '💡' },
  { section: 'Energy', prompt: 'How is your energy? What drained or energised you?', icon: '🔋' },
  { section: 'Tomorrow', prompt: 'What\'s the one thing you want to accomplish tomorrow?', icon: '🎯' },
];

function formatToday(): string {
  const d = new Date();
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function compileContent(answers: string[]): string {
  return REFLECTION_PROMPTS
    .map((p, i) => ({ heading: p.section, icon: p.icon, text: answers[i] }))
    .filter((s) => s.text.trim())
    .map((s) => `## ${s.icon} ${s.heading}\n${s.text.trim()}`)
    .join('\n\n');
}

export default function GuidedReflectionDialog({
  open,
  onClose,
  onSaved,
  date,
  trigger = 'Manual',
}: GuidedReflectionDialogProps) {
  const [answers, setAnswers] = useState<string[]>(REFLECTION_PROMPTS.map(() => ''));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleAnswerChange = useCallback(
    (index: number) => (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setAnswers((prev) => {
        const next = [...prev];
        next[index] = event.target.value;
        return next;
      });
    },
    [],
  );

  const handleAnswerTranscript = useCallback(
    (index: number) => (text: string) => {
      setAnswers((prev) => {
        const next = [...prev];
        next[index] = text;
        return next;
      });
    },
    [],
  );

  const handleReset = useCallback(() => {
    setAnswers(REFLECTION_PROMPTS.map(() => ''));
    setError(null);
  }, []);

  const handleSubmit = useCallback(async () => {
    setSaving(true);
    setError(null);

    const finalContent = compileContent(answers);
    if (!finalContent.trim()) {
      setError('Please fill in at least one section before saving.');
      setSaving(false);
      return;
    }

    const insights = answers[2].trim() || undefined;
    const adjustmentsMade = answers[4].trim() || undefined;
    const reflectionDate = date || formatToday();

    try {
      await reflectionsApi.create({
        date: reflectionDate,
        trigger,
        content: finalContent,
        insights,
        adjustmentsMade,
      });
      onSaved();
      handleReset();
    } catch (err: any) {
      setError(
        err.response?.data?.message ||
          err.message ||
          'Failed to save reflection. Please try again.',
      );
    } finally {
      setSaving(false);
    }
  }, [answers, date, trigger, onSaved, handleReset]);

  const handleClose = useCallback(() => {
    onClose();
    // Delay reset so the dialog close animation completes before clearing state
    setTimeout(handleReset, 300);
  }, [onClose, handleReset]);

  const hasAnyContent = answers.some((a) => a.trim().length > 0);

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) handleClose(); }}>
      <DialogContent className="max-h-[85vh] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Guided Reflection</DialogTitle>
          <p className="text-sm text-muted-foreground">
            Take a few minutes to reflect on your day. Fill in as many or as few sections as you like.
          </p>
        </DialogHeader>
        <div className="flex flex-col gap-3">
          {error && (
            <Alert variant="error">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {REFLECTION_PROMPTS.map((prompt, index) => (
            <Card
              key={prompt.section}
              className={cn(
                'border-2 transition-colors',
                answers[index].trim() ? 'border-primary' : 'border-border',
              )}
            >
              <CardContent className="pt-4">
                <div className="mb-3 flex items-center gap-2">
                  <span className="text-2xl leading-none">{prompt.icon}</span>
                  <div>
                    <p className="text-sm font-semibold">{prompt.section}</p>
                    <p className="text-sm text-muted-foreground">{prompt.prompt}</p>
                  </div>
                </div>
                <DictationTextField
                  multiline
                  rows={3}
                  fullWidth
                  placeholder={`Your thoughts on ${prompt.section.toLowerCase()}...`}
                  value={answers[index]}
                  onChange={handleAnswerChange(index)}
                  onTranscriptChange={handleAnswerTranscript(index)}
                  size="small"
                />
              </CardContent>
            </Card>
          ))}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={handleClose} disabled={saving}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            disabled={saving || !hasAnyContent}
            className="gap-1.5"
          >
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            {saving ? 'Saving...' : 'Save Reflection'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
